import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apagar, apagarJson, atualizarJson, enviarJson, pedirJson } from "./api";
import { esquemaSinalizador } from "./tickets";

/* Operação do agente: pesquisa global, favoritos e sinalizadores, exportações em segundo plano,
   impressão do chamado e o acesso ao catálogo de serviços. O contrato é o de
   `api/app/dominios/operacao/`. As chaves de consulta dos chamados começam em `["tickets"]`, então
   o que `tickets.ts` invalida (responder, alterar) atualiza estas telas também. */

const CHAVE_DOS_CHAMADOS = ["tickets"] as const;
const uuid = z.string().uuid();

/* ------------------------------------------------------------------ pesquisa global */

export const TAMANHO_MINIMO_DA_BUSCA = 2;

const esquemaItemDaBusca = z.object({
  id: uuid,
  titulo: z.string(),
  detalhe: z.string().nullable().optional(),
  organizacao_id: uuid.nullable().optional(),
  organizacao: z.string().nullable().optional(),
  achado_em: z.string().nullable().optional(),
  estado: z.string().nullable().optional(),
});

const esquemaGrupoDaBusca = z.object({
  chave: z.enum(["chamados", "organizacoes", "pessoas", "artigos", "ativos"]),
  rotulo: z.string(),
  itens: z.array(esquemaItemDaBusca),
  mais: z.boolean(),
});

const esquemaResultadoDaBusca = z.object({
  termo: z.string(),
  grupos: z.array(esquemaGrupoDaBusca),
});

export type ItemDaBusca = z.infer<typeof esquemaItemDaBusca>;
export type GrupoDaBusca = z.infer<typeof esquemaGrupoDaBusca>;
export type ResultadoDaBusca = z.infer<typeof esquemaResultadoDaBusca>;

export const ONDE_O_TERMO_APARECEU: Record<string, string> = {
  numero: "pelo número",
  assunto: "no assunto",
  descricao: "na descrição",
  conversa: "na conversa",
};

/** Para onde cada resultado leva. Só o que o lado da pessoa consegue abrir. */
export function destinoDoResultado(
  grupo: GrupoDaBusca["chave"],
  item: ItemDaBusca,
  interno: boolean,
  podeEditarArtigos: boolean,
): string {
  const area = interno ? "/admin" : "/app";
  switch (grupo) {
    case "chamados":
      return `${area}/chamados/${item.id}`;
    case "organizacoes":
      return `/admin/cadastros/organizacoes/${item.id}`;
    case "pessoas":
      return item.organizacao_id
        ? `/admin/cadastros/organizacoes/${item.organizacao_id}`
        : "/admin/cadastros";
    case "artigos":
      return interno
        ? podeEditarArtigos
          ? `/admin/conhecimento/${item.id}`
          : "/admin/conhecimento"
        : `/app/ajuda/${item.id}`;
    case "ativos":
      return `${area}/ativos?busca=${encodeURIComponent(item.titulo)}`;
  }
}

export function useBuscaGlobal(termo: string) {
  const limpo = termo.trim();
  return useQuery({
    queryKey: ["busca-global", limpo],
    queryFn: ({ signal }) =>
      pedirJson(`/busca?q=${encodeURIComponent(limpo)}`, esquemaResultadoDaBusca, { signal }),
    enabled: limpo.length >= TAMANHO_MINIMO_DA_BUSCA,
    staleTime: 10_000,
  });
}

/* ------------------------------------------------------- favoritos e sinalizadores */

export const CORES_DO_SINALIZADOR = [
  "vermelho",
  "laranja",
  "amarelo",
  "verde",
  "azul",
  "roxo",
] as const;
export type CorDoSinalizador = (typeof CORES_DO_SINALIZADOR)[number];
export type Sinalizador = z.infer<typeof esquemaSinalizador>;

export const ROTULO_DA_COR: Record<CorDoSinalizador, string> = {
  vermelho: "Vermelho",
  laranja: "Laranja",
  amarelo: "Amarelo",
  verde: "Verde",
  azul: "Azul",
  roxo: "Roxo",
};

/** A cor que a bandeira pinta na tela. Variáveis do tema não existem para estas seis — valor fixo. */
export const TINTA_DA_COR: Record<CorDoSinalizador, string> = {
  vermelho: "#c0392b",
  laranja: "#d9731a",
  amarelo: "#b8860b",
  verde: "#2e8b57",
  azul: "#2a6fbb",
  roxo: "#7d4cb8",
};

const esquemaFavorito = z.object({ favorito: z.boolean() });

function invalidarChamados(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: CHAVE_DOS_CHAMADOS });
}

export function useAlternarFavorito(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: async (favoritar: boolean) => {
      const caminho = `/tickets/${encodeURIComponent(ticketId)}/favorito`;
      return favoritar
        ? atualizarJson(caminho, esquemaFavorito, {})
        : apagarJson(caminho, esquemaFavorito);
    },
    onSuccess: () => invalidarChamados(cliente),
  });
}

export function useDefinirSinalizador(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { cor: CorDoSinalizador; motivo: string }) =>
      atualizarJson(
        `/tickets/${encodeURIComponent(ticketId)}/sinalizador`,
        esquemaSinalizador,
        corpo,
      ),
    onSuccess: () => invalidarChamados(cliente),
  });
}

export function useRetirarSinalizador(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => apagar(`/tickets/${encodeURIComponent(ticketId)}/sinalizador`),
    onSuccess: () => invalidarChamados(cliente),
  });
}

/* ------------------------------------------------------------------- exportações */

/** O recorte de um relatório como o pedido de exportação o entende (só o que está preenchido). */
export function recorteDaExportacao(filtros: {
  inicio: string;
  fim: string;
  organizacao_id: string;
  servico: string;
  equipe_id: string;
  responsavel_id: string;
  urgencia: string;
  agrupar_por: string;
}): Record<string, string> {
  return Object.fromEntries(Object.entries(filtros).filter(([, valor]) => valor !== ""));
}


export const TIPOS_DE_EXPORTACAO = [
  "fila",
  "indicadores",
  "permanencia",
  "chamados_do_indicador",
] as const;
export type TipoDeExportacao = (typeof TIPOS_DE_EXPORTACAO)[number];

export const ESTADOS_DA_EXPORTACAO = ["pendente", "gerando", "pronta", "falhou", "expirada"] as const;
export type EstadoDaExportacao = (typeof ESTADOS_DA_EXPORTACAO)[number];

export const ROTULO_DO_ESTADO_DA_EXPORTACAO: Record<EstadoDaExportacao, string> = {
  pendente: "Na fila",
  gerando: "Gerando",
  pronta: "Pronta",
  falhou: "Não foi possível gerar",
  expirada: "Link vencido",
};

const esquemaExportacao = z.object({
  id: uuid,
  tipo: z.enum(TIPOS_DE_EXPORTACAO),
  rotulo: z.string(),
  estado: z.enum(ESTADOS_DA_EXPORTACAO),
  nome_arquivo: z.string().nullable(),
  tamanho: z.number().int().nullable(),
  linhas: z.number().int().nullable(),
  erro: z.string().nullable(),
  criada_em: z.string(),
  pronta_em: z.string().nullable(),
  expira_em: z.string().nullable(),
  baixada_em: z.string().nullable(),
  link: z.string().nullable().optional(),
});

export type Exportacao = z.infer<typeof esquemaExportacao>;

/** O que se pede: o tipo e os filtros do recorte (os mesmos nomes da fila e dos relatórios). */
export type PedidoDeExportacao = { tipo: TipoDeExportacao } & Record<
  string,
  string | boolean | undefined
>;

/** Enquanto algo está na fila ou sendo gerado, a lista se atualiza sozinha. */
export const INTERVALO_DA_EXPORTACAO_EM_ANDAMENTO = 3000;

export function emAndamento(exportacoes: readonly Exportacao[] | undefined): boolean {
  return Boolean(exportacoes?.some((item) => item.estado === "pendente" || item.estado === "gerando"));
}

export function useMinhasExportacoes(enabled = true) {
  return useQuery({
    queryKey: ["exportacoes"],
    queryFn: ({ signal }) => pedirJson("/exportacoes", z.array(esquemaExportacao), { signal }),
    enabled,
    refetchInterval: (consulta) =>
      emAndamento(consulta.state.data) ? INTERVALO_DA_EXPORTACAO_EM_ANDAMENTO : false,
  });
}

export function usePedirExportacao() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (pedido: PedidoDeExportacao) => {
      const limpo = Object.fromEntries(
        Object.entries(pedido).filter(([, valor]) => valor !== undefined && valor !== ""),
      );
      return enviarJson("/exportacoes", esquemaExportacao, limpo);
    },
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ["exportacoes"] }),
  });
}

/* --------------------------------------------------------------------- impressão */

const esquemaAcaoImpressa = z.object({
  id: uuid,
  tipo: z.enum(["publica", "interna"]),
  texto: z.string(),
  autor_email: z.string().nullable(),
  canal: z.string(),
  criada_em: z.string(),
  origem_numero: z.number().int().nullable().optional(),
});

const esquemaImpressao = z.object({
  numero: z.number().int(),
  assunto: z.string(),
  descricao: z.string(),
  organizacao: z.string(),
  solicitante: z.string().nullable(),
  responsavel: z.string().nullable(),
  equipe: z.string().nullable(),
  estado: z.string(),
  urgencia: z.string(),
  categoria: z.string().nullable(),
  servico: z.string().nullable(),
  criado_em: z.string(),
  resolvido_em: z.string().nullable(),
  campos: z.array(z.tuple([z.string(), z.string()])),
  acoes: z.array(esquemaAcaoImpressa),
  anexos: z.array(z.object({ id: uuid, nome: z.string(), interno: z.boolean() })),
  com_notas_internas: z.boolean(),
  impresso_por: z.string(),
  impresso_em: z.string(),
  sinalizador: esquemaSinalizador.nullable().optional(),
});

export type ChamadoParaImpressao = z.infer<typeof esquemaImpressao>;

export function useChamadoParaImpressao(ticketId: string, comNotasInternas: boolean) {
  return useQuery({
    queryKey: [...CHAVE_DOS_CHAMADOS, ticketId, "impressao", comNotasInternas],
    queryFn: ({ signal }) =>
      pedirJson(
        `/tickets/${encodeURIComponent(ticketId)}/impressao?notas_internas=${comNotasInternas}`,
        esquemaImpressao,
        { signal },
      ),
    enabled: Boolean(ticketId),
  });
}

/* --------------------------------------------------------- catálogo restrito */

const esquemaServicoDisponivel = z.object({
  id: uuid,
  nome: z.string(),
  categoria_id: uuid,
  categoria: z.string(),
});

const esquemaAcessoDoServico = z.object({
  organizacoes: z.array(uuid),
  equipes: z.array(uuid),
});

export type ServicoDisponivel = z.infer<typeof esquemaServicoDisponivel>;
export type AcessoDoServico = z.infer<typeof esquemaAcessoDoServico>;

/** Os serviços que a pessoa pode escolher; a equipe passa a organização em nome da qual abre. */
export function useServicosDisponiveis(organizacaoId?: string, enabled = true) {
  return useQuery({
    queryKey: [...CHAVE_DOS_CHAMADOS, "servicos-disponiveis", organizacaoId ?? ""],
    queryFn: ({ signal }) =>
      pedirJson(
        `/tickets/catalogo/servicos/disponiveis${
          organizacaoId ? `?organizacao_id=${encodeURIComponent(organizacaoId)}` : ""
        }`,
        z.array(esquemaServicoDisponivel),
        { signal },
      ),
    enabled,
    retry: false,
  });
}

export function useAcessoDoServico(servicoId: string, enabled = true) {
  return useQuery({
    queryKey: [...CHAVE_DOS_CHAMADOS, "servicos", servicoId, "acesso"],
    queryFn: ({ signal }) =>
      pedirJson(
        `/tickets/catalogo/servicos/${encodeURIComponent(servicoId)}/acesso`,
        esquemaAcessoDoServico,
        { signal },
      ),
    enabled: enabled && Boolean(servicoId),
  });
}

export function useDefinirAcessoDoServico(servicoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: AcessoDoServico) =>
      atualizarJson(
        `/tickets/catalogo/servicos/${encodeURIComponent(servicoId)}/acesso`,
        esquemaAcessoDoServico,
        corpo,
      ),
    onSuccess: () => invalidarChamados(cliente),
  });
}
